import type { TFunction } from 'i18next';
import { toJS } from 'mobx';
import { computedFn } from 'mobx-utils';
import numeral from 'numeral';

import type {
  Account,
  Campaign,
  IChannelBudgets,
  OmniCampaign,
  Segment,
  Targetable,
  User,
} from '@feathr/blackbox';
import {
  CAMPAIGN_MAX_BUDGET_INTERNAL,
  CampaignClass,
  CampaignState,
  EIntegrationTypes,
  getMaxTargetValue,
} from '@feathr/blackbox';
import { moment } from '@feathr/hooks';
import { findRachisError } from '@feathr/rachis';

import type { ICampaignValidationErrors } from '../../../CampaignSummary';
import {
  getAllocationPercentage,
  isSplitBelowFloor,
} from './ChannelBudgetSplit/ChannelBudgetSplit.utils';

/**
 * True when a budget recommendation was rejected because the campaign's minimum
 * budget exceeds the account's maximum campaign budget (the backend returns a
 * `BudgetExceedsAccountMaxError`). Shared by every caller of
 * `getRecommendedBudget` so they agree on which failures are the account-max
 * case and which are generic.
 */
export function isBudgetExceedsAccountMaxError(error: unknown): boolean {
  return (
    findRachisError(error, (entry) => !!entry.code?.includes('BudgetExceedsAccountMaxError')) !==
    undefined
  );
}

export const getAudienceSize: (
  campaign: Campaign,
  segments: Segment[],
  targetables: Targetable[],
) => number = computedFn((campaign: Campaign, segments: Segment[], targetables: Targetable[]) => {
  const cls: CampaignClass = campaign.get('_cls');

  // Retargeting or Meta Retargeting
  if ([CampaignClass.Segment, CampaignClass.Facebook, CampaignClass.Omni].includes(cls)) {
    return segments.map((s) => s.reachable).reduce((acc, c) => acc + c, 0);
  }

  // Email Mapping or Meta Email Mapping
  if ([CampaignClass.EmailList, CampaignClass.EmailListFacebook].includes(cls)) {
    return targetables.reduce((acc, t) => {
      acc += t.get('num_unique_emails') || 0;
      return acc;
    }, 0);
  }

  // We use these numbers to scale the recommend budget by small, medium, and large expected audiences sizes
  switch (cls) {
    // Campaigns that target a specific audience are huge, so we limit the audience size to 100k to prevent an offensive recommended budget

    // Legacy Lookalike
    case CampaignClass.Lookalike:

    // Current Lookalike
    case CampaignClass.SeedSegment:

    case CampaignClass.Affinity: {
      return 100000;
    }

    // Search and Geo-like campaigns have smaller audiences on the order of 10s of thousands.

    // Search Keyword
    case CampaignClass.Search:

    // Historical Geofencing
    case CampaignClass.MobileGeoFencing:

    // Mobile Geofencing
    case CampaignClass.MobileGeoFenceRetargeting: {
      return 10000;
    }

    default: {
      // 1000 for default since first party groups tend to be pretty small
      return 1000;
    }
  }
});

// This calculation mirrors the incoming backend calculation for the number of days in a campaign
export function getDays(campaign: Campaign): number {
  const dateStart = campaign.get('date_start');
  const dateEnd = campaign.get('date_end');
  if (!(dateStart && dateEnd)) {
    return 0;
  }
  const startMoment = moment.utc(dateStart, moment.ISO_8601);
  const endMoment = moment.utc(dateEnd, moment.ISO_8601);

  const duration = moment.duration(endMoment.diff(startMoment));
  const hours = duration.asHours();
  const numDays = Math.ceil(hours / 24);

  return numDays;
}

export function getMinBudget(campaign: Campaign, channelMinimums?: IChannelBudgets | null): number {
  if (campaign.isMonetization) {
    return getDays(campaign) * 4000;
  }

  /*
   * The backend owns the minimum budget (it accounts for the whole-number split,
   * Meta hard floor, Display soft). We use its total once the recommendation
   * loads; before that — or if the recommendation failed — we don't invent a
   * frontend estimate. Returning 0 means nothing fabricated is enforced or
   * shown; the backend is the sole source of truth for the minimum.
   */
  return channelMinimums?.total ?? 0;
}

export const getRecommendedImpressions: (
  campaign: Campaign,
  segments: Segment[],
  targetables: Targetable[],
) => number = computedFn((campaign: Campaign, segments: Segment[], targetables: Targetable[]) => {
  // Impressions are just for monetization campaigns
  if (!campaign.isMonetization) {
    return 0;
  }
  const audienceSize = getAudienceSize(campaign, segments, targetables);
  const reach = 0.015;
  const freqCap = campaign.get('bidding_strategy').freq_cap;
  const days = getDays(campaign);
  return audienceSize * reach * freqCap * days;
});

export function getRecommendedBid(size: number): number {
  /*
   * This will be revisited at a later date. Intentionally leaving the multiple
   * if statements that evaluate to the same number.
   */
  if (size > 20000) {
    return 5;
  }
  if (size < 20000 && size > 5000) {
    return 5;
  }
  return 10;
}

interface IValidateStepBudgetProps {
  campaign: Campaign;
  account: Account;
  user?: User;
  noMaxBudget?: boolean;
  isOnProduction: boolean;
  /** Backend per-channel/total minimum budgets; null until the recommendation loads. */
  channelMinimums?: IChannelBudgets | null;
  /**
   * True when the recommendation was rejected because the campaign's minimum
   * budget exceeds the account maximum. Blocks the wizard — the campaign can't
   * be funded within the account limit.
   */
  budgetExceedsAccountMax?: boolean;
  /**
   * True while a budget recommendation request is in flight. The per-channel
   * split is recomputing, so block Next/Publish until it settles rather than
   * letting the user advance with a stale split.
   */
  isRecommendationLoading?: boolean;
  /**
   * True when the budget recommendation failed (generic error). Without it there
   * are no minimums to validate against, so block the wizard rather than let the
   * user advance with an unvalidated budget.
   */
  budgetRecommendationFailed?: boolean;
  t: TFunction;
}

/**
 * Validates the Display/Meta budget split for a dual-channel Omni campaign.
 * Returns the message to surface under the channel split, or null when the
 * split is valid (or can't yet be judged — e.g. before the backend per-channel
 * minimums load). Extracted from validateStepBudget to keep that function's
 * cognitive complexity in check.
 */
function getDualChannelSplitError(
  campaign: OmniCampaign,
  t: TFunction,
  channelMinimums?: IChannelBudgets | null,
): string | null {
  const channelSplit = campaign.get('exposure_settings')?.channel_split;
  if (!channelSplit?.allocations?.length) {
    return t('Configure a budget split between Display and Meta channels');
  }

  /*
   * Both channels must clear their backend minimum. Only enforced once the
   * recommendation has loaded — the frontend has no representative estimate of
   * the per-channel minimums, so it doesn't guess. (isSplitBelowFloor skips the
   * check at the minimum budget, where the split is locked to the valid
   * recommended one.)
   */
  const totalValue = campaign.get('exposure_settings')?.target_value ?? 0;
  if (!channelMinimums || totalValue <= 0) {
    return null;
  }

  const displayAlloc = getAllocationPercentage(
    channelSplit.allocations,
    EIntegrationTypes.Tradedesk,
  );
  const metaAlloc = getAllocationPercentage(channelSplit.allocations, EIntegrationTypes.Meta);
  if (displayAlloc === undefined || metaAlloc === undefined) {
    return null;
  }

  const displayBudget = (totalValue * displayAlloc) / 100;
  const metaBudget = (totalValue * metaAlloc) / 100;
  if (!isSplitBelowFloor({ displayBudget, metaBudget, channelMinimums })) {
    return null;
  }

  return t(
    'Each channel must meet its minimum: Display {{display}}, Meta {{meta}}. Adjust the split or raise the total budget.',
    {
      display: numeral(channelMinimums.display).format('$0,0'),
      meta: numeral(channelMinimums.meta).format('$0,0'),
    },
  );
}

export function validateStepBudget({
  campaign,
  account,
  noMaxBudget,
  isOnProduction,
  channelMinimums,
  budgetExceedsAccountMax,
  isRecommendationLoading,
  budgetRecommendationFailed,
  t,
}: IValidateStepBudgetProps): ICampaignValidationErrors {
  const monetization = campaign.get('parent_kind') === 'partner';

  const attributes = ['date_start', 'date_end', 'state', '_cls'];
  if (monetization) {
    attributes.push('monetization_value');
  }
  if (campaign.isOmniCampaign) {
    attributes.push('exposure_settings');
  }

  const modelErrors = toJS(campaign.validate(attributes, false, 'grouped').errors) as Record<
    string,
    string[]
  >;

  const errors = {
    budget: [] as string[],
    channel_split: [] as string[],
    date_start: [] as string[],
    date_end: [] as string[],
    monetization_value: [] as string[],
    /*
     * We have to pass along state and _cls so they show up in the attributes provided
     * to the date_start validator
     */
    ...modelErrors,
  };

  /*
   * The campaign's minimum budget exceeds the account maximum — it can't be
   * funded, so block the wizard (both Next and Publish). Keep this to a single
   * sentence: it's what shows in the Next-button validation tooltip. The full
   * remedy copy lives in the Budget-card alert.
   */
  if (budgetExceedsAccountMax) {
    errors.budget.push(
      t(
        'This campaign’s minimum budget exceeds your account’s maximum campaign budget of {{max}}.',
        {
          max: numeral(getMaxTargetValue(account.campaignsMaxBudget, monetization)).format('$0,0'),
        },
      ),
    );
  }

  /*
   * A recommendation request is in flight (e.g. the budget just changed and the
   * split is recomputing). Block Next/Publish until it settles so the user can't
   * advance on a stale split.
   */
  if (isRecommendationLoading) {
    errors.budget.push(t('Calculating the recommended budget. Please wait a moment.'));
  }

  /*
   * The recommendation failed, so we have no minimums to validate the budget
   * against. Block the wizard until a retry succeeds rather than letting the
   * user advance with an unvalidated budget.
   */
  if (budgetRecommendationFailed) {
    errors.budget.push(
      t("We couldn't calculate a recommended budget. Adjust the campaign and try again."),
    );
  }

  /*
   * Map exposure_settings errors to channel_split so the UI can display them
   * under the ChannelBudgetSplit component.
   */
  if (modelErrors.exposure_settings?.length) {
    errors.channel_split = [...errors.channel_split, ...modelErrors.exposure_settings];
    delete (errors as Record<string, unknown>).exposure_settings;
  }

  /*
   * Additional guard: verify allocations are present for dual-channel Omni campaigns.
   * The model constraint checks field presence, but this also catches the edge case
   * where channel_split exists but has empty allocations.
   */
  if (campaign.isOmniCampaign && !errors.channel_split.length) {
    const omni = campaign as OmniCampaign;
    if (omni.isDualChannel) {
      const splitError = getDualChannelSplitError(omni, t, channelMinimums);
      if (splitError) {
        errors.channel_split.push(splitError);
      }
    }
  }

  const canEditBudget = !!campaign.get('date_start') && !!campaign.get('date_end');
  const isDraft = campaign.get('state') === CampaignState.Draft;
  const budget = campaign.get('exposure_settings').target_value || 0;
  const minBudget = getMinBudget(campaign, channelMinimums);
  const minRemainingBudget = minBudget - budget;
  const spend: number = campaign.get('stats_cache')?.spend || 0;
  const exposureSettingsHaveChanged = campaign.isAttributeDirty('exposure_settings');
  // Campaign dates affect minimum budget calculation, so we need to validate when they change
  const campaignDatesHaveChanged =
    campaign.isAttributeDirty('date_start') || campaign.isAttributeDirty('date_end');
  /*
   *  Allow editing goals once spend has exceeded budget.
   *  https://github.com/Feathr/shrike/issues/1998
   *
   *  This previously did not take into account the state of the campaign,
   *  allowing the user to set a zero budget, save as draft, reload the
   *  page and publish with a zero budget.
   */
  const addErrorsAfterPublishedIfBudgetIsTouched =
    !isDraft && (exposureSettingsHaveChanged || campaignDatesHaveChanged);

  const targetType = campaign.get('exposure_settings').target_type;
  if (targetType === 'fixed_budget' && !budget) {
    errors.budget.push(t('Budget cannot be $0.'));
  }

  const isCapped = isOnProduction && account.get('is_internal');

  // Internal accounts are capped at $25 if on production to prevent spending real money accidentally
  if (isCapped && budget > CAMPAIGN_MAX_BUDGET_INTERNAL) {
    errors.budget.push(
      t('Internal accounts can only create campaigns with budgets ${{max}} and under.', {
        max: CAMPAIGN_MAX_BUDGET_INTERNAL,
      }),
    );
  }

  if (
    budget < spend &&
    budget >= minBudget &&
    !monetization &&
    addErrorsAfterPublishedIfBudgetIsTouched
  ) {
    /*
     * Prevent user from setting a budget below campaign spend.
     * Do not include draft campaigns because a draft campaign
     * should never have spend associated with it.
     */
    errors.budget.push(
      t('Budget must be at least ${{spend}} to account for the amount already spent.', {
        spend: numeral(spend).format('0,0.00'),
      }),
    );
  } else if (
    // Prevent budgets less than the minimum budget. This applies to every account
    // regardless of internal status or environment; isCapped governs only the maximum.
    budget < minBudget &&
    !monetization &&
    canEditBudget &&
    (addErrorsAfterPublishedIfBudgetIsTouched || isDraft)
  ) {
    errors.budget.push(
      t(
        'Total budget must be at least {{min}}. Add {{remaining}} to fulfill this campaign for its duration.',
        {
          min: numeral(minBudget).format('$0,0.00'),
          remaining: numeral(minRemainingBudget).format('$0,0.00'),
        },
      ),
    );
  }

  /*
   *We can ignore max budget under the following circumstances:
   *- The noMaxBudget feature flag is set
   */
  if (!noMaxBudget) {
    const maxBudget = getMaxTargetValue(account.campaignsMaxBudget, monetization);
    if (budget > maxBudget) {
      if (monetization) {
        errors.budget.push(
          t('The number of impressions cannot exceed {{max}}.', {
            max: numeral(maxBudget).format('0,0'),
          }),
        );
      } else {
        errors.budget.push(
          t('The budget cannot exceed {{max}} (account-wide setting).', {
            max: numeral(maxBudget).format('$0,0'),
          }),
        );
      }
    }
  }

  return errors;
}
